<template>
  <div>
    <b-table v-if="gradeHelpers.gradesTableFields"
             :current-page="currentPage"
             :fields="gradeHelpers.gradesTableFields"
             :items="proposalsFromProp"
             :per-page="perPage"
             :sort-by="'document_specification.document_id'"
             :sort-desc='true'
             :tbody-tr-class="setRowClass"
             details-td-class="pa-0"
             head-variant="light"
             hover
             stacked="lg"
             striped
             @row-clicked="setShowFinalSubmit">

      <template v-slot:top-row="{fields}">
        <proposal-filter :id="parent"
                         v-model="proposalsFromProp"
                         :fields="fields"
                         :not-include-key="['actions', 'ordinalNumber']"/>
      </template>

      <template v-slot:cell(ordinalNumber)="row">
        <base-column-ordinal-number v-bind="{index: row.index, perPage: perPage, currentPage: currentPage}"/>
      </template>
      <template v-slot:cell(experiment_color.evaluated_color)="row">
        <strong v-if="row.item.experiment_color"
                :class="'text-' + getExperimentColor(row.item.experiment_color.evaluated_color)">
          {{ row.item.experiment_color.evaluated_color }}
        </strong>
      </template>
      <template v-slot:cell(final_grade.acceptance_mark)="row">
        <traffic-lights-buttons v-if="row.item.final_grade && row.item.final_grade.acceptance_mark"
                                v-model="row.item.final_grade.acceptance_mark"
                                :options="trafficLightsOptionsLimited"
                                disabled
                                stacked/>
        <traffic-lights-buttons v-else-if="isScientificMerit(row.item)"
                                :options="trafficLightsOptionsLimited"
                                disabled
                                stacked
                                value=""/>
        <div v-else>N/A</div>
      </template>
      <template v-slot:cell(management_evaluation.acceptance_mark)="row">
        <traffic-lights-buttons v-model="row.item.management_evaluation.acceptance_mark"
                                :options="trafficLightsOptionsFull"
                                disabled
                                stacked/>
      </template>
      <template v-slot:cell(actions)="row">
        <b-row>
          <b-col class="default-actions"
                 @click="setShowFinalSubmit(row.item, undefined, $event)"
                 @keyup.down="setShowFinalSubmit(row.item, undefined, $event)">
            <base-action-expand-details :b-table-row="row"
                                        @click="handleDetailsShowing(row)"/>

            <action-download-pdf :proposal="row.item"/>

            <base-material-design-icon-button v-b-modal.rapidAccessGradeModal
                                              :title="isEditionAllowed(row.item) ? 'Edit grade' : 'View grade'"
                                              icon="fact_check"
                                              @click="setProposal(row.item)"/>

            <action-open-proposal-full-details :proposal="row.item"/>

            <base-double-click-submit-button v-if="isSubmissionAllowed(row.item) && !isManagementEvaluation"
                                             :btn-options="submitBtnOptions"
                                             :is-confirmation="isFinalSubmit(row.item)"
                                             class="d-block ml-auto mr-4"
                                             @click="submitGrade(row.item)"/>

            <base-icon-button-send v-if="isSubmissionAllowed(row.item) && isManagementEvaluation"
                                   class="d-block ml-auto"
                                   title="Submit evaluation and email User"
                                   @click="openConfirmationModal(row.item)"/>
          </b-col>
        </b-row>
      </template>
      <template v-slot:row-details="row">
        <base-details-container :key="getId(row.item)">

          <template v-slot:details-body>
            <proposal-show-details-row :is-rapid-access="true" :proposal="row.item"/>
          </template>

          <template v-slot:details-footer>
            <base-collapse-block-button button-text="Hide details"
                                        @click="handleDetailsShowing(row)"/>
          </template>

        </base-details-container>
      </template>
    </b-table>

    <b-table-footer v-model="loadingFromProp"
                    :current-page="currentPage"
                    :data-length="proposalsFromProp.length"
                    :per-page="perPage"
                    @change-page="changePage">
      <template v-slot>
        <base-per-page-input v-model="perPage"/>
      </template>
    </b-table-footer>
    <b-modal id="rapidAccessGradeModal"
             :ref="modalRef"
             :title="modalTitle"
             bg-variant="light"
             header-bg-variant="dark"
             header-text-variant="white"
             hide-footer
             lazy
             size="full-response"
             @hidden="closeModal">
      <rapid-access-grade-modal v-model="proposal"
                                :isManagementEvaluation="isManagementEvaluation"
                                :isUserEditor="isUserEditor"
                                @close-modal="closeModalSlot"/>
    </b-modal>
    <base-submit-danger-modal ref="submitConfirmationModal"
                              :confirm-function="() => submitGrade()"
                              class="modal-class"
                              confirm-button-title="Confirm"
                              modal-title="Confirm submission and emailing User">
      <p class="my-1 p-1 text-center">Do you want to submit evaluation of this proposal?</p>
      <p class="p-1 custom-text-danger text-center">
        Submitting will <b>automatically email the User</b> and cannot be revoked!
      </p>
    </base-submit-danger-modal>
  </div>
</template>

<script>
  import ActionDownloadPdf from '@/components/actions/ActionDownloadPdf';
  import BaseActionExpandDetails from '@/components/actions/BaseActionExpandDetails';
  import ActionOpenProposalFullDetails from '@/components/actions/ActionOpenProposalFullDetails';
  import TrafficLightsButtons from '@/components/traffic_lights_buttons';
  import trafficLightsOptions from '@/json/trafficLightsAcceptanceMark';
  import trafficLightsOptionsSafety from '@/json/trafficLightsAcceptanceMarkSafety';
  import submitBtnOptions from '@/json/doubleClickSubmitButton';
  import RapidAccessGradeModal from '@/components/evaluation/RapidAccessGradeModal';
  import ProposalFilter from '@/components/ProposalFilter';
  import BTableFooter from '@/components/BTableFooter';
  import BaseDetailsContainer from '@/components/base-components/BaseDetailsContainer';
  import ProposalShowDetailsRow from '@/components/evaluation/ProposalShowDetailsRow';
  import BaseCollapseBlockButton from '@/components/base-components/BaseCollapseBlockButton';
  import { experiment } from '@/mixins/experiments';
  import { infrastructureMixin } from '@/mixins/infrastructure';
  import { openedDetails } from '@/mixins/openedDetails';
  import BaseColumnOrdinalNumber from '@/components/base-components/BaseColumnOrdinalNumber';
  import BaseDoubleClickSubmitButton from '@/components/base-components/BaseDoubleClickSubmitButton';
  import BaseIconButtonSend from '@/components/base-components/base-icons/BaseIconButtonSend';
  import BaseMaterialDesignIconButton from '@/components/base-components/base-icons/BaseMaterialDesignIconButton';
  import BaseSubmitDangerModal from '@/components/base-components/base-modals/BaseSubmitDangerModal';
  import {
    isScientificMerit,
    setDangerRowIfEvaluationOverdue,
  } from '@/helpers/evaluation/rapid-access/rapidAccessEvaluations';
  import BasePerPageInput from '@/components/base-components/BasePerPageInput';
  import { underscoresToHyphens } from '@/helpers';

  export default {
    name: 'RapidAccessGradeTable',
    components: {
      BaseMaterialDesignIconButton,
      BaseSubmitDangerModal,
      BaseIconButtonSend,
      ActionOpenProposalFullDetails,
      BaseActionExpandDetails,
      ActionDownloadPdf,
      BaseDoubleClickSubmitButton,
      RapidAccessGradeModal,
      ProposalFilter,
      BTableFooter,
      ProposalShowDetailsRow,
      TrafficLightsButtons,
      BaseCollapseBlockButton,
      BaseDetailsContainer,
      BaseColumnOrdinalNumber,
      BasePerPageInput,
    },
    mixins: [experiment, infrastructureMixin, openedDetails],
    props: {
      gradeHelpers: {
        type: Object,
        required: true,
      },
      isManagementEvaluation: {
        type: Boolean,
        default: false,
      },
      loading: {
        type: Boolean,
        default: false,
      },
      parent: {
        type: Number,
        default: 0,
      },
      proposals: {
        type: Array,
        default: () => [],
      },
      userElevatedPermissions: {
        type: Object,
        required: true,
        validator(property) {
          const requiredKeys = ['isEditor', 'isSubmitter'];
          const propertyKeys = Object.keys(property);

          const areRequiredKeysIncluded = requiredKeys.every((el) => propertyKeys.includes(el));
          const areRequiredValuesBoolean = requiredKeys.every((key) => typeof property[key] === 'boolean');
          return areRequiredKeysIncluded && areRequiredValuesBoolean;
        },
      },
    },
    data() {
      return {
        proposal: {},
        currentPage: 1,
        perPage: 20,
        proposalsFromProp: [],
        modalRef: 'modalRef',
        trafficLightsOptionsFull: trafficLightsOptions,
        trafficLightsOptionsSafety,
        submitBtnOptions,
        finalSubmit: '',
        proposalToSubmitManagementEvaluation: {},
      };
    },
    computed: {
      gradeType() {
        return this.isManagementEvaluation ? 'management_evaluation' : 'final_grade';
      },
      isUserEditor() {
        return this.userElevatedPermissions.isEditor;
      },
      gradeTypeUrl() {
        return underscoresToHyphens(this.gradeType);
      },
      loadingFromProp() {
        return this.loading;
      },
      modalTitle() {
        return this.isManagementEvaluation
          ? 'Rapid access management evaluation form'
          : 'Rapid access final grade form';
      },
      trafficLightsOptionsLimited() {
        return [trafficLightsOptions[0], trafficLightsOptions[trafficLightsOptions.length - 1]];
      },
    },
    watch: {
      proposals() {
        // Update proposals when proposal filter input has value
        // Also empty the proposalsFromProp list when changing call (All calls option is exception)
        const renderedProposalsIds = this.getRenderedProposalsIds();
        this.updateRenderedProposals(renderedProposalsIds);

        this.updateProposalsOnInitialization();
      },
      proposalsFromProp: {
        handler(newValue) {
          this.$emit('filtered-proposals', newValue);
        },
      },
    },
    methods: {
      changePage(val) {
        this.currentPage = val;
      },
      closeModal() {
        const index = this.proposalsFromProp.findIndex((el) => this.getId(el) === this.getId(this.proposal));
        if (index >= 0) {
          this.$set(this.proposalsFromProp, index, this.proposal);
          this.$emit('update-proposal', this.proposal);
        }
      },
      editable(proposal) {
        if (proposal[this.gradeType].states) {
          return !(proposal[this.gradeType].states.name.includes('submitted'));
        }
        return true;
      },
      getRenderedProposalsIds() {
        return this.proposalsFromProp.map((proposal) => this.getId(proposal));
      },
      isEditionAllowed(proposal) {
        if (!this.userElevatedPermissions.isEditor) return false;
        return this.editable(proposal);
      },
      isFinalSubmit(proposal) {
        return this.getId(proposal) === this.finalSubmit;
      },
      setShowFinalSubmit(proposal, unusedIndex, event) {
        this.finalSubmit = '';
        if (event.target.id === this.submitBtnOptions.attempt.icon.id) {
          this.finalSubmit = this.getId(proposal);
        }
      },
      isSubmissionAllowed(proposal) {
        if (!this.userElevatedPermissions.isSubmitter) return false;
        return this.editable(proposal);
      },
      openConfirmationModal(item) {
        this.proposalToSubmitManagementEvaluation = item;
        this.$refs.submitConfirmationModal.show();
      },
      closeConfirmationModal() {
        this.$refs.submitConfirmationModal.hide();
      },
      setProposal(proposal) {
        this.proposal = proposal;
      },
      async submitGrade(item = this.proposalToSubmitManagementEvaluation) {
        const proposalId = this.getId(item);
        try {
          this.closeConfirmationModal();
          const response = await this.axios.patch(
            `/documents/rapid-access/${proposalId}/${this.gradeTypeUrl}/${this.infrastructure}`,
          );
          // eslint-disable-next-line no-param-reassign
          item[this.gradeType].states.name = 'submitted';
          this.handleSetExperimentNotification(response.data.message);
        } catch (error) {
          if (error.response?.data?.error) {
            this.$notify({ type: 'error', title: error.response.data.error });
            if (error.response.data.error.includes('email not sent')) {
              item[this.gradeType].states.name = 'submitted';
            }
          } else {
            this.$notify({ type: 'error', title: 'Communication error' });
          }
        }
      },
      updateProposalsOnInitialization() {
        this.proposalsFromProp = this.proposals;
      },
      updateRenderedProposals(renderedProposalsIds) {
        this.proposalsFromProp = this.proposals.filter((proposal) => renderedProposalsIds.includes(this.getId(proposal)));
      },
      isGradeOverdue(proposal) {
        const evaluation = this.isManagementEvaluation ? proposal?.management_evaluation : proposal?.final_grade;
        if (evaluation) {
          return !!setDangerRowIfEvaluationOverdue(proposal, evaluation);
        }
        return false;
      },
      setRowClass(item, type) {
        if (this.isGradeOverdue(item)) {
          return type === 'row' ? 'table-danger' : '';
        }
        return this.highlightOpenedProposals(item, type);
      },
      getExperimentColor(status) {
        let variant = 'secondary';
        this.trafficLightsOptionsSafety.forEach((option) => {
          if (option.value === status) {
            variant = option.disabledVariant;
          }
        });
        return variant;
      },
      isScientificMerit(proposal) {
        return isScientificMerit(proposal);
      },
    },
  };
</script>

<style scoped>
  .default-actions {
    padding: 0;
    margin-left: 0.7rem;
  }
</style>
